import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X, FileText, Play, BookOpen, Wrench, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";

const GROUP_META = [
  { key: "content", label: "Stories & Videos", icon: FileText },
  { key: "series", label: "Series", icon: Play },
  { key: "rulebook", label: "Rulebook", icon: BookOpen },
  { key: "tools", label: "Tools", icon: Wrench },
  { key: "products", label: "Shop", icon: ShoppingBag },
];
const PER_GROUP = 4;

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      setQ("");
      setGroups(null);
      setActive(-1);
    }
  }, [open]);

  // debounced live search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setGroups(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term } });
        setGroups(data.groups);
        setActive(-1);
      } catch (e) {
        setGroups(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  // flatten for keyboard nav
  const flat = [];
  if (groups) {
    GROUP_META.forEach((g) => (groups[g.key] || []).slice(0, PER_GROUP).forEach((r) => flat.push(r)));
  }
  const totalCount = groups
    ? GROUP_META.reduce((n, g) => n + (groups[g.key]?.length || 0), 0)
    : 0;

  const goAll = useCallback(() => {
    if (q.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      onClose();
    }
  }, [q, navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && flat[active]?.url) {
        navigate(flat[active].url);
        onClose();
      } else {
        goAll();
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-[color:var(--vt-dark)]/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="search-overlay"
    >
      <div
        role="search"
        className="bg-[color:var(--vt-dark)] vt-grain border-b border-[color:var(--vt-rule-dark)]"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-6">
          <div className="flex items-center gap-4 border-b-2 border-[color:var(--vt-rule-dark)] pb-3">
            <SearchIcon size={26} className="text-[color:var(--vt-amber)] shrink-0" />
            <label htmlFor="site-search-input" className="sr-only">Search stories, series, tools</label>
            <input
              id="site-search-input"
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search stories, series, tools…"
              data-testid="search-overlay-input"
              autoComplete="off"
              className="flex-1 min-w-0 bg-transparent border-0 outline-none font-head uppercase text-2xl lg:text-4xl text-[color:var(--vt-white)] placeholder:text-[color:var(--vt-graphite)]"
            />
            <button
              onClick={onClose}
              aria-label="Close search"
              data-testid="search-overlay-close"
              className="text-[color:var(--vt-white)] hover:text-[color:var(--vt-amber)] transition-colors shrink-0"
            >
              <X size={28} />
            </button>
          </div>

          <div aria-live="polite" className="sr-only">
            {groups ? `${totalCount} results for ${q}` : ""}
          </div>

          <div className="mt-4 max-h-[65vh] overflow-y-auto" data-testid="search-overlay-results">
            {q.trim().length >= 2 && !loading && totalCount === 0 && (
              <p className="py-8 text-[color:var(--vt-graphite)]" data-testid="search-overlay-empty">
                No call on that one. Press Enter to see suggestions.
              </p>
            )}
            {groups &&
              GROUP_META.map((g) => {
                const items = (groups[g.key] || []).slice(0, PER_GROUP);
                if (!items.length) return null;
                return (
                  <div key={g.key} className="mb-6" data-testid={`search-group-${g.key}`}>
                    <div className="vt-eyebrow mb-2 flex items-center gap-2">
                      <g.icon size={13} className="text-[color:var(--vt-amber)]" /> {g.label}
                    </div>
                    <ul>
                      {items.map((r, i) => {
                        const idx = flat.indexOf(r);
                        const on = idx === active;
                        return (
                          <li key={`${g.key}-${i}`}>
                            <button
                              onClick={() => { navigate(r.url); onClose(); }}
                              onMouseEnter={() => setActive(idx)}
                              data-testid={`search-result-${g.key}-${i}`}
                              className={`w-full text-left px-3 py-3 border-l-2 transition-colors ${
                                on
                                  ? "border-[color:var(--vt-amber)] bg-white/5"
                                  : "border-transparent hover:bg-white/5"
                              }`}
                            >
                              <span className="font-head text-lg text-[color:var(--vt-white)] leading-tight block">
                                {r.title}
                              </span>
                              {r.snippet && (
                                <span className="text-sm text-[color:var(--vt-graphite)] line-clamp-1 block mt-0.5">
                                  {r.snippet}
                                </span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            {totalCount > 0 && (
              <button
                onClick={goAll}
                data-testid="search-overlay-see-all"
                className="mt-2 mb-2 font-head uppercase tracking-wide text-sm text-[color:var(--vt-amber)] hover:text-[color:var(--vt-amber-hover)] transition-colors"
              >
                See all results →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
